/*
 * This component implments the form  component for continuity across the app. Error handling included.
 *
 * @author Cargomatic Engineering
 * @usage <CargomatiSelect />
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { getClassName } from '../../utils/ui/classes';
import Dropdown from './forms/Dropdown';
import Label from './forms/Label';
import ErrorLabel from './forms/ErrorLabel';

const CargomaticSelect = (props) => {
  const {
    className,
    items,
    selected,
    value,
    onSelect,
    hideLabel,
    hideError,
    label,
    disabled,
    info,
    error,
    caption,
    showCaption,
    valueKey,
    textKey,
    applyDefaultMargins,
    inputClassNames,
    inputStyles,
    labelClassName,
    dataTestId,
  } = props;
  const { t } = useTranslation();
  const containerClassNames = getClassName(
    {
      error: error && error.message,
      'mb-4': applyDefaultMargins,
    },
    className,
  );

  function renderLabel() {
    if (hideLabel) {
      return null;
    }

    const defaultClassName = { 'mb-1': info, 'mb-2': !info };
    const className = labelClassName ?? defaultClassName;
    return (
      (<Label dataTestId="Label-c4d65532" helptext={info} className={className}>
        {label}
      </Label>)
    );
  }

  const getSelectedItem = (selectedValue) => {
    if (!selectedValue) {
      if (valueKey) {
        return {};
      }
      return '';
    }
    let filteredItem;
    if (valueKey) {
      filteredItem = items.find((item) => item[valueKey] === selectedValue);
    } else {
      filteredItem = selectedValue;
    }
    return filteredItem;
  };

  const handleSelect = (selectedValue) => {
    onSelect(getSelectedItem(selectedValue));
  };

  const localizeItem = (item) => {
    let display_text = textKey ? item[textKey] : item;
    display_text =
      textKey && textKey === 'localizationSlug'
        ? t(display_text?.toUpperCase(), {
            count: item?.count || 1,
          })
        : display_text;
    if (item.titleize) display_text = titleize(display_text);
    return display_text;
  };

  const selectedItem = getSelectedItem(selected || value);
  const _selectedValue =
    selectedItem && valueKey ? selectedItem[valueKey] : selectedItem;
  /**
   * NOTE: If selected item is empty object and valueKey have some value then
   *       add empty string for fall back
   *  */
  const selectedValue = _selectedValue || '';
  return (
    (<div data-testid={dataTestId} className={containerClassNames}>
      {renderLabel()}
      <Dropdown
        className={getClassName({ 'mt-1': !hideLabel }, inputClassNames)}
        inputStyles={inputStyles}
        selected={selectedValue}
        onSelect={handleSelect}
        caption={caption}
        disabled={disabled}
        showCaption={showCaption}
        dataTestId={dataTestId}
      >
        {items.map((item, index) => {
          const optionValue = valueKey ? item[valueKey] : item;
          return (
            (<option data-testid={`option-e99e0724-${index}`} key={index} value={optionValue}>
              {localizeItem(item)}
            </option>)
          );
        })}
      </Dropdown>
      {!hideError && error && error.message && (
        <ErrorLabel dataTestId="ErrorLabel-b6d2fa67" className="mt-1 mb-4">{error.message}</ErrorLabel>
      )}
    </div>)
  );
};

CargomaticSelect.propTypes = {
  items: PropTypes.array,
  selected: PropTypes.string,
  value: PropTypes.string,
  onSelect: PropTypes.func,
  label: PropTypes.string,
  hideLabel: PropTypes.bool,
  hideError: PropTypes.bool,
  info: PropTypes.string,
  showCaption: PropTypes.bool,
  caption: PropTypes.string,
  valueKey: PropTypes.string,
  textKey: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  labelClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  inputClassNames: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  inputStyles: PropTypes.object, // React CSS Style Object
  applyDefaultMargins: PropTypes.bool,
};

CargomaticSelect.defaultProps = {
  items: [],
  hideLabel: false,
  hideError: false,
  showCaption: true,
  caption: 'Select',
  className: {},
  applyDefaultMargins: false,
};

export default withTranslation()(CargomaticSelect);
